'use client';

import Link from 'next/link';

interface ComplianceScore {
  framework: string;
  tool_id: string;
  score: number;
  last_assessment: string | null;
  status: 'compliant' | 'partial' | 'non-compliant' | 'not-assessed';
}

interface ComplianceHealthWidgetProps {
  scores: ComplianceScore[];
  loading?: boolean;
}

export function ComplianceHealthWidget({ scores, loading }: ComplianceHealthWidgetProps) {
  if (loading) {
    return (
      <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
        <h3 className="text-lg font-semibold text-light-text dark:text-foreground mb-4">Compliance Health</h3>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-light-border dark:bg-background rounded" />
          ))}
        </div>
      </div>
    );
  }

  const getStatusColor = (status: ComplianceScore['status']) => {
    switch (status) {
      case 'compliant':
        return 'bg-green-500';
      case 'partial':
        return 'bg-yellow-500';
      case 'non-compliant':
        return 'bg-red-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status: ComplianceScore['status']) => {
    switch (status) {
      case 'compliant':
        return 'Compliant';
      case 'partial':
        return 'Partial';
      case 'non-compliant':
        return 'Non-Compliant';
      default:
        return 'Not Assessed';
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="bg-light-surface dark:bg-surface rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-light-text dark:text-foreground">Compliance Health</h3>
        <Link href="/tools" className="text-sm text-accent hover:underline">
          Run Assessment
        </Link>
      </div>

      {scores.length === 0 ? (
        <div className="text-center py-8 text-light-text-secondary dark:text-foreground-tertiary">
          <div className="text-4xl mb-2">📋</div>
          <p className="text-sm">No compliance assessments yet</p>
          <Link href="/tools" className="text-accent text-sm hover:underline mt-2 inline-block">
            Start your first assessment
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {scores.map((score) => (
            <div key={score.tool_id} className="flex items-center gap-3">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-light-text dark:text-foreground">
                    {score.framework}
                  </span>
                  <span className="text-xs text-light-text-secondary dark:text-foreground-tertiary">
                    {formatDate(score.last_assessment)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-light-border dark:bg-background rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${getStatusColor(score.status)}`}
                      style={{ width: `${score.score}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-light-text-secondary dark:text-foreground-secondary w-12 text-right">
                    {score.score}%
                  </span>
                </div>
              </div>
              <div
                className={`px-2 py-0.5 rounded text-xs font-medium text-white ${getStatusColor(score.status)}`}
              >
                {getStatusLabel(score.status)}
              </div>
            </div>
          ))}
        </div>
      )}

      {scores.length > 0 && (
        <div className="mt-4 pt-4 border-t border-light-border dark:border-border-muted">
          <div className="flex items-center justify-between text-sm">
            <span className="text-light-text-secondary dark:text-foreground-tertiary">Average Score</span>
            <span className="font-semibold text-light-text dark:text-foreground">
              {Math.round(scores.reduce((sum, s) => sum + s.score, 0) / scores.length)}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
